import '@calcom/atoms/globals.min.css';
import { BookerEmbed } from '@calcom/atoms';
import { CAL_COM_NAME } from '@krishanmadan/core/src/model/const';
import { useToggleable } from '@epihub/web/src/hooks/useToggleable';
import * as React from 'react';
import { Structure } from '@epihub/web/src/components/Structure';
import { FloorReflection } from '@epihub/web/src/components/Reflect';
import { OverlaySkeleton } from '@epihub/web/src/components/OverlaySkeleton';
import { Skeleton, Group, Stack } from '@mantine/core';
import { useState } from 'react';
import { useGlobalStore } from '../../store/useGlobalStore';
import { styled, useTheme } from '../../theme/theme';
import { BookingSuccessView } from './CalendarSuccessView';
import { CalendarErrorView } from './CalendarErrorView';

type Props = {
  eventSlug: string;
};

export const Calendar = ({ eventSlug }: Props) => {
  const theme = useTheme();
  const isMobile = theme.screen.width.px < 769;
  const visitorName = useGlobalStore((store) => store.settings.visitorName);
  const { val: loading, setFalse: onLoaded, setTrue: onLoad } = useToggleable(true);
  const [bookingState, setBookingState] = useState<null | true | false>(null);
  return (
    <OverlaySkeleton
      renderChildrenHidden
      loading={loading}
      skeleton={isMobile ? <CalendarSkeletonMobile /> : <CalendarSkeleton />}
    >
      <Structure>
        {bookingState === true && <BookingSuccessView />}
        {bookingState === false && (
          <CalendarErrorView
            onRetry={() => {
              onLoad();
              setBookingState(null);
            }}
          />
        )}
        {bookingState === null && (
          <Styles>
            <FloorReflection
              skew={-15}
              reflectionHeight='75%'
              fadeRate={75}
              disabled={isMobile}
            >
              <BookerEmbed
                hideBranding
                username={CAL_COM_NAME}
                eventSlug={eventSlug}
                view={isMobile ? 'COLUMN_VIEW' : 'MONTH_VIEW'}
                defaultFormValues={{
                  name: visitorName,
                }}
                customClassNames={{
                  bookerContainer: 'border-subtle border',
                }}
                onCreateBookingSuccess={(data) => {
                  if (data.status === 'success') {
                    setBookingState(true);
                  } else {
                    setBookingState(false);
                  }
                }}
                onTimeslotsLoaded={onLoaded}
              />
            </FloorReflection>
          </Styles>
        )}
      </Structure>
    </OverlaySkeleton>
  );
};

const Styles = styled.div`
  position: relative;

  .calcom-atoms .my-20 {
    margin-block: 0;
  }

  /* Select element that has: child > child > child > the specific link */
  *:has(> * > a[href="https://go.cal.com/booking"]) {
    display: none;
  }

  /* Select sticky header 2 parents up from event-meta and remove stickiness */
  *:has(> * > [data-testid="event-meta"]) {
    position: static !important;
  }

  .calcom-atoms .rounded-md {
    border-top-left-radius: var(--radius-md);
    border-top-right-radius: var(--radius-md);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  [data-testid="booker-container"] {
    box-shadow: 0 20px 40px rgba(0,255,255,0.25);
  }
  [data-testid="day"] {
    cursor: pointer;
    :hover {
      box-shadow: 0 20px 40px rgba(0,0,0,0.25);
    }
    :active {
      transform: scale(0.50);
    }
  }

  /* Mobile styles */
  @media (max-width: 768px) {
    [data-testid="booker-container"] {
      max-width: 100%;
      overflow-x: hidden;
      border-radius: var(--radius-md);
    }

    .calcom-atoms {
      max-width: 90vw;
      overflow-x: hidden;
    }
  }
`;

export const CalendarSkeleton = () => (
  <Structure hfull wfull hcenter vcenter>
    <Structure p={12} py={18} row w={1020}>
      {/* Left sidebar - User info and meeting details */}
      <Stack w={180} gap={4}>
        <Skeleton height={36} width={36} radius='md' />
        <Skeleton height={18} width={150} radius='sm' />
        <Skeleton height={26} mt={6} width={116} radius='sm' />
        <Skeleton height={22} mt={4} width={70} radius='sm' />
        <Skeleton height={18} mt={10} width={130} radius='sm' />
        <Skeleton height={20} mt={14} width={140} radius='sm' />
      </Stack>

      {/* Center - Calendar grid */}
      <Stack gap={4} ml={98} mt={2}>
        {/* Month header and navigation */}
        <Group justify='space-between'>
          <Skeleton height={20} width={120} radius='sm' />
          <Skeleton height={20} width={40} radius='sm' />
        </Group>

        {/* Day headers */}
        <Group gap={17} wrap='nowrap' mt={24} mb={8} ml={2}>
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} height={20} width={48} radius='sm' />)}
        </Group>

        {/* Calendar grid - 5 rows of 7 days */}
        {Array.from({ length: 5 }).map((_, row) => (
          <Group key={row} gap={17} wrap='nowrap' mt={8}>
            {Array.from({ length: 7 }).map((_, col) => <Skeleton key={col} height={48} width={48} radius='sm' />)}
          </Group>
        ))}
      </Stack>

      {/* Right side - Time slots */}
      <Stack w={100} gap={8} ml={38}>
        {/* Date header and toggle */}
        <div
          style={{
            width: 240,
            marginBottom: 8,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Skeleton mt={4} height={18} width={50} radius='sm' />
          <Skeleton height={34} width={50} radius='sm' />
        </div>

        {/* Time slots */}
        {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} height={40} width={240} radius='sm' />)}
      </Stack>
    </Structure>
  </Structure>
);

export const CalendarSkeletonMobile = () => (
  <Structure hfull wfull hcenter vcenter>
    <Stack w='100%' gap={16} p={8}>
      {/* User info header */}
      <Stack gap={8}>
        <Group gap={12}>
          <Skeleton height={40} width={40} radius='md' />
          <Stack gap={4}>
            <Skeleton height={16} width={120} radius='sm' />
            <Skeleton height={14} width={80} radius='sm' />
          </Stack>
        </Group>
        <Skeleton height={20} width='60%' radius='sm' />
        <Skeleton height={16} width='40%' radius='sm' />
      </Stack>

      {/* Date selector row */}
      <Group gap={8} wrap='nowrap' style={{ overflowX: 'hidden' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Stack key={i} gap={4} align='center'>
            <Skeleton height={14} width={32} radius='sm' />
            <Skeleton height={40} width={40} radius='xl' />
          </Stack>
        ))}
      </Group>

      {/* Time slots */}
      <Stack gap={8} w='100%'>
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={44} width='100%' radius='sm' />)}
      </Stack>
    </Stack>
  </Structure>
);
